import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, saveAuth } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("El enlace no contiene un token válido. Solicita uno nuevo.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      saveAuth(data.token, data.user);
      toast.success("Contraseña actualizada. ¡Bienvenido de nuevo!");
      const dest =
        data.user.role === "admin"
          ? "/admin"
          : data.user.role === "customer"
          ? "/mi-cuenta"
          : "/worker";
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No se pudo restablecer la contraseña.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <BrandHeader />
      <main className="max-w-md mx-auto px-6 py-16 md:py-24">
        <div className="glass rounded-xl p-8 md:p-10" data-testid="reset-password-panel">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-yellow-400 font-mono-display mb-3">
            <ShieldCheck className="w-4 h-4" />
            Restablecer contraseña
          </div>
          <h1 className="font-display font-black text-3xl mb-2">Nueva contraseña</h1>
          <p className="text-zinc-400 mb-8 text-sm">
            Elige una nueva contraseña para tu cuenta. El enlace expira en 30 minutos y sólo puede usarse una vez.
          </p>

          {!token ? (
            <div className="border border-red-500/40 bg-red-500/10 rounded-lg p-4 text-red-200 text-sm" data-testid="reset-invalid-token">
              El enlace no contiene un token válido. Solicita uno nuevo desde la página de inicio de sesión.
              <div className="mt-4">
                <Link
                  to="/login"
                  className="text-yellow-400 hover:text-yellow-300 underline text-sm"
                  data-testid="reset-back-login"
                >
                  Volver al login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="np" className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
                  Nueva contraseña
                </Label>
                <Input
                  id="np"
                  data-testid="reset-new-password-input"
                  type="password"
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cp" className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
                  Confirmar contraseña
                </Label>
                <Input
                  id="cp"
                  data-testid="reset-confirm-password-input"
                  type="password"
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="reset-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                {loading ? "Actualizando..." : "Guardar nueva contraseña"}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-xs text-zinc-500 hover:text-yellow-400 underline"
                  data-testid="reset-back-login-link"
                >
                  Volver al login
                </Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
